// ---------------------------------------------------------------------------
// Admin module — Merchant Review Page
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { MerchantRiskDashboard } from '../components/MerchantRiskDashboard';
import { MerchantMonitoringEvents } from '../components/MerchantMonitoringEvents';

// ---------------------------------------------------------------------------
// Types — shapes returned by the API
// ---------------------------------------------------------------------------

interface MerchantProfile {
  id: string;
  tenant_id: string;
  legal_name: string;
  trading_name: string | null;
  mcc_code: string | null;
  website_url: string | null;
  status: string;
  risk_level: string | null;
  monitoring_cadence: string | null;
  created_at: string;
  updated_at: string;
}

interface RiskFactor {
  category: string;
  score: number;
  weight: number;
  details: string;
}

interface RiskProfile {
  overallScore: number;
  factors: RiskFactor[];
  lastAssessedAt: string | null;
  nextReviewAt: string | null;
}

interface MonitoringEvent {
  id: string;
  eventType: string;
  severity: string;
  details: string;
  detectedAt: string;
  resolvedAt: string | null;
}

// ---------------------------------------------------------------------------
// Decision actions
// ---------------------------------------------------------------------------

type DecisionType = 'approve' | 'reject' | 'request_resubmission' | 'suspend';

const DECISION_VARIANTS: Record<DecisionType, 'danger' | 'warning'> = {
  approve: 'warning',
  reject: 'danger',
  request_resubmission: 'warning',
  suspend: 'danger',
};

const DECISION_STYLES: Record<DecisionType, string> = {
  approve: 'bg-primary hover:bg-primary-hover text-white',
  reject: 'bg-danger hover:bg-danger text-white',
  request_resubmission:
    'bg-warning hover:bg-warning text-white',
  suspend: 'bg-ink-muted hover:bg-ink-surface2 text-white',
};

const DECISION_KEYS: DecisionType[] = ['approve', 'reject', 'request_resubmission', 'suspend'];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MerchantReviewPage() {
  const { t } = useTranslation('admin');
  const { profileId } = useParams<{ profileId: string }>();
  const navigate = useNavigate();

  const [merchant, setMerchant] = useState<MerchantProfile | null>(null);
  const [riskProfile, setRiskProfile] = useState<RiskProfile | null>(null);
  const [events, setEvents] = useState<MonitoringEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [pendingDecision, setPendingDecision] = useState<DecisionType | null>(null);

  const loadMerchant = useCallback(() => {
    if (!profileId) return;
    setLoading(true);
    setError(null);

    // Use raw fetch since the merchant endpoints may not be in the typed api-client
    const baseUrl = (api as unknown as { baseUrl?: string }).baseUrl ?? '';

    Promise.all([
      fetch(`${baseUrl}/v1/admin/merchant-profiles/${profileId}`).then((r) =>
        r.json(),
      ),
      fetch(
        `${baseUrl}/v1/admin/merchant-profiles/${profileId}/risk-signals`,
      ).then((r) => r.json()),
    ])
      .then(([profileRes, signalsRes]) => {
        const profileData = (profileRes as { data: MerchantProfile }).data;
        setMerchant(profileData);

        // Build risk profile from actual API risk signals
        const signals = (signalsRes as { data: Array<Record<string, unknown>> })
          .data;
        if (signals && signals.length > 0) {
          // Compute risk factors from real signal data
          const factors: RiskFactor[] = signals.map((s) => ({
            category: (s.signal_type as string) ?? 'unknown',
            score: typeof s.score === 'number' ? (s.score as number) : 0,
            weight: typeof s.weight === 'number' ? (s.weight as number) : (1 / signals.length),
            details: (s.details as string) ?? (s.signal_type as string) ?? 'Risk signal',
          }));

          // Compute weighted overall score from real data
          const totalWeight = factors.reduce((sum, f) => sum + f.weight, 0);
          const overallScore = totalWeight > 0
            ? Math.round(factors.reduce((sum, f) => sum + f.score * f.weight, 0) / totalWeight)
            : 0;

          // Find the most recent assessment timestamp from signals
          const signalDates = signals
            .map((s) => s.detected_at as string | undefined)
            .filter(Boolean) as string[];
          const lastAssessedAt = signalDates.length > 0
            ? signalDates.sort().reverse()[0]
            : null;

          setRiskProfile({
            overallScore,
            factors,
            lastAssessedAt,
            nextReviewAt: null,
          });

          setEvents(
            signals.map((s) => ({
              id: (s.id as string) ?? '',
              eventType: (s.signal_type as string) ?? 'unknown',
              severity: (s.severity as string) ?? 'medium',
              details: (s.details as string) ?? `Risk signal: ${(s.signal_type as string) ?? 'unknown'}`,
              detectedAt: (s.detected_at as string) ?? new Date().toISOString(),
              resolvedAt: (s.resolved_at as string) ?? null,
            })),
          );
        } else {
          setRiskProfile(null);
          setEvents([]);
        }
      })
      .catch((err: unknown) => {
        const message =
          err instanceof Error ? err.message : t('merchantReview.errors.loadFailed');
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [profileId, t]);

  useEffect(() => {
    loadMerchant();
  }, [loadMerchant]);

  const handleDecision = useCallback(
    async (decisionType: DecisionType) => {
      if (!profileId) return;
      setDecisionLoading(true);

      try {
        const baseUrl =
          (api as unknown as { baseUrl?: string }).baseUrl ?? '';
        await fetch(`${baseUrl}/v1/admin/merchant-profiles/${profileId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: decisionType }),
        });

        // Refresh data
        loadMerchant();
      } catch {
        setError(t('merchantReview.messages.decisionFailed'));
      } finally {
        setDecisionLoading(false);
      }
    },
    [profileId, loadMerchant, t],
  );

  if (loading) return <LoadingState rows={8} />;
  if (error) return <ErrorState message={error} onRetry={loadMerchant} />;
  if (!merchant) return <ErrorState message={t('merchantReview.errors.notFound')} />;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-2 text-sm text-ink-muted hover:text-ink"
          >
            &larr; {t('merchantReview.nav.back')}
          </button>
          <h1 className="font-display text-3xl text-ink">
            {merchant.legal_name}
          </h1>
          {merchant.trading_name && (
            <p className="text-sm text-ink-muted">
              {t('merchantReview.fields.dba', { name: merchant.trading_name })}
            </p>
          )}
        </div>
        <StatusBadge value={merchant.status} />
      </div>

      {/* Merchant overview */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-sm border border-hairline bg-surface-raised p-4">
          <p className="text-xs font-semibold uppercase text-ink-muted">{t('merchantReview.fields.mcc')}</p>
          <p className="mt-1 text-lg font-medium text-ink">
            {merchant.mcc_code ?? '--'}
          </p>
        </div>
        <div className="rounded-sm border border-hairline bg-surface-raised p-4">
          <p className="text-xs font-semibold uppercase text-ink-muted">
            {t('merchantReview.fields.website')}
          </p>
          <p className="mt-1 truncate text-lg font-medium text-ink">
            {merchant.website_url ? (
              <a
                href={merchant.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-primary underline"
              >
                {merchant.website_url}
              </a>
            ) : (
              '--'
            )}
          </p>
        </div>
        <div className="rounded-sm border border-hairline bg-surface-raised p-4">
          <p className="text-xs font-semibold uppercase text-ink-muted">
            {t('merchantReview.fields.riskLevel')}
          </p>
          <p className="mt-1 text-lg font-medium text-ink">
            {merchant.risk_level ?? t('merchantReview.fields.notAssessed')}
          </p>
        </div>
        <div className="rounded-sm border border-hairline bg-surface-raised p-4">
          <p className="text-xs font-semibold uppercase text-ink-muted">
            {t('merchantReview.fields.monitoring')}
          </p>
          <p className="mt-1 text-lg font-medium text-ink">
            {merchant.monitoring_cadence ?? '--'}
          </p>
        </div>
      </div>

      {/* Risk dashboard */}
      {riskProfile ? (
        <div className="mb-6">
          <MerchantRiskDashboard
            overallScore={riskProfile.overallScore}
            factors={riskProfile.factors}
            lastAssessedAt={riskProfile.lastAssessedAt}
            nextReviewAt={riskProfile.nextReviewAt}
          />
        </div>
      ) : (
        <div className="mb-6 rounded-sm border border-hairline bg-surface-sunken p-6 text-center text-sm text-ink-muted">
          {t('merchantReview.messages.riskUnavailable')}
        </div>
      )}

      {/* Monitoring events */}
      <div className="mb-6">
        <MerchantMonitoringEvents events={events} />
      </div>

      {/* Decision actions */}
      <div className="rounded-sm border border-hairline bg-surface-raised p-6">
        <h3 className="mb-4 text-sm font-semibold uppercase text-ink-muted">
          {t('merchantReview.headings.actions')}
        </h3>
        <div className="flex flex-wrap gap-3">
          {DECISION_KEYS.map((decision) => (
            <button
              key={decision}
              type="button"
              onClick={() => setPendingDecision(decision)}
              disabled={decisionLoading}
              className={`rounded-sm px-5 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${DECISION_STYLES[decision]}`}
            >
              {t(`merchantReview.decisions.${decision}.label` as const)}
            </button>
          ))}
        </div>
      </div>

      <ConfirmDialog
        open={pendingDecision !== null}
        title={t('merchantReview.decisions.confirmTitle', {
          label: pendingDecision ? t(`merchantReview.decisions.${pendingDecision}.label` as const) : '',
        })}
        message={pendingDecision ? t(`merchantReview.decisions.${pendingDecision}.message` as const) : ''}
        confirmLabel={pendingDecision ? t(`merchantReview.decisions.${pendingDecision}.label` as const) : t('merchantReview.decisions.confirmFallback')}
        variant={pendingDecision ? DECISION_VARIANTS[pendingDecision] : 'danger'}
        onConfirm={() => {
          if (pendingDecision) {
            handleDecision(pendingDecision);
          }
          setPendingDecision(null);
        }}
        onCancel={() => setPendingDecision(null)}
      />
    </div>
  );
}
